import { Icons } from 'app/constants/icons';
import { useNotificationsStore } from 'app/hooks/useNotificationStore';
import { Button } from 'app/shared/buttons/Button';
import { notificationVariants } from 'app/theme/notification';
import { cn } from 'app/utils/styles.utils';
import {
  HTMLProps,
  ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';


export const _DEFAULT_DURATION = 2500;

export type NotificationType = 'success' | 'error' | 'custom';

export type NotificationId = string;

export type NotificationContent = ReactNode;

export type NotificationItem = Omit<
  HTMLProps<HTMLDivElement>,
  'children' | 'content'
> & {
  id: NotificationId;
  variant: NotificationType;
  content?: NotificationContent;
  close?: boolean;
  duration?: number; // Duration in milliseconds
};

type NotificationProps = {
  notification: NotificationItem;
};

export const Notification = ({notification}: NotificationProps) => {
  const {
    id,
    variant,
    content,
    duration = 0,
    close = false,
    className,
    ...props
  } = notification;
  const [isClosing, setIsClosing] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null!);

  const removeNotification = useNotificationsStore(
    state => state.removeNotification,
  );

  const handleAnimationEnd = useCallback(
    () => removeNotification(id),
    [removeNotification, id],
  );

  const handleClose = useCallback(() => {
    setIsClosing(true);

    containerRef.current.addEventListener('animationend', handleAnimationEnd);
  }, [handleAnimationEnd]);

  useEffect(() => {
    const container = containerRef.current;
    const parsedDuration = duration;

    const handleAnimationEnd = () => removeNotification(id);

    const timeoutId =
      duration !== Infinity
        ? setTimeout(() => {
            setIsClosing(true);

            container.addEventListener('animationend', handleAnimationEnd);
          }, parsedDuration)
        : null;

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }

      container.removeEventListener('animationend', handleAnimationEnd);
      setIsLoaded(false);
      setIsClosing(false);
    };
  }, [id, duration, removeNotification]);

  return (
    <div
      role="alert"
      aria-live="assertive"
      ref={containerRef}
      className={cn(
        notificationVariants({
          variant,
        }),
        className,
        {
          'animate-notification-in': !isLoaded && !isClosing,
          'animate-notification-out': isClosing,
        },
      )}
      {...props}>
      {content}

      {close && (
        <Button
          onClick={handleClose}
          className="absolute right-3.75 top-3.75 flex">
          <Icons.close
            className="stroke-neutral-300 light:stroke-black"
            size="17"
            strokeWidth={3}
          />
        </Button>
      )}
    </div>
  );
};
